import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Notification } from 'rsuite';
import { PATCHNegotiationDetailsSaveEditedDatapoint } from '../../../../DetalhesDaNegociacao/DetalhesDaNegociacao.services';
import { NegotiationSupplierDatapointStatus, NegotitationRowDataProps } from '../../../NegociacaoFornecedor.types';
import { getEditedDatapointModel } from '../../../utils';

export interface UseStatusToggleProps {
  rowData: NegotitationRowDataProps;
  onStatusChange?: (newStatus: NegotiationSupplierDatapointStatus, updatedRowData: NegotitationRowDataProps) => void;
}

export interface UseStatusToggleReturn {
  isLoading: boolean;
  currentStatus: NegotiationSupplierDatapointStatus;
  handleApprove: () => void;
  handleReject: () => void;
  error: string | null;
}

export const useStatusToggle = ({ rowData, onStatusChange }: UseStatusToggleProps): UseStatusToggleReturn => {
  const [error, setError] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<NegotiationSupplierDatapointStatus>(rowData.negotiationSupplierDatapointStatus || null);

  useEffect(() => {
    setCurrentStatus(rowData.negotiationSupplierDatapointStatus || null);
  }, [rowData.negotiationSupplierDatapointStatus]);

  const { mutate: updateStatus, isPending } = useMutation({
    mutationFn: async (newStatus: NegotiationSupplierDatapointStatus) => {
      const datapointModel = {
        ...getEditedDatapointModel(rowData),
        negotiationSupplierDatapointStatus: newStatus,
      };

      const updatedDatapoint = await PATCHNegotiationDetailsSaveEditedDatapoint(datapointModel);

      return { newStatus, updatedDatapoint };
    },
    onMutate: async (newStatus: NegotiationSupplierDatapointStatus) => {
      const previousStatus = currentStatus;

      setCurrentStatus(newStatus);

      const updatedRowData: NegotitationRowDataProps = {
        ...rowData,
        negotiationSupplierDatapointStatus: newStatus,
      };
      onStatusChange?.(newStatus, updatedRowData);

      return { previousStatus };
    },
    onSuccess: ({ newStatus, updatedDatapoint }) => {
      setError(null);

      setCurrentStatus(newStatus);

      const updatedRowData: NegotitationRowDataProps = {
        ...rowData,
        negotiationSupplierDatapointStatus: newStatus,
        isEdited: updatedDatapoint.isEdited,
      };
      onStatusChange?.(newStatus, updatedRowData);

      const statusText = newStatus === 'APPROVED' ? 'aprovado' : newStatus === 'DECLINED' ? 'reprovado' : 'removido';
      Notification.success({
        description: `Status ${statusText} com sucesso`,
      });
    },
    onError: (err, _newStatus, context) => {
      console.error('Error updating negotiation status:', err);
      setError('Erro ao atualizar status da negociação');

      if (context?.previousStatus !== undefined) {
        setCurrentStatus(context.previousStatus);

        const rolledBackRowData: NegotitationRowDataProps = {
          ...rowData,
          negotiationSupplierDatapointStatus: context.previousStatus,
        };
        onStatusChange?.(context.previousStatus, rolledBackRowData);
      }

      Notification.error({
        description: 'Erro ao atualizar status da negociação',
      });
    },
  });

  const handleApprove = () => {
    const newStatus: NegotiationSupplierDatapointStatus = currentStatus === 'APPROVED' ? null : 'APPROVED';
    updateStatus(newStatus);
  };

  const handleReject = () => {
    const newStatus: NegotiationSupplierDatapointStatus = currentStatus === 'DECLINED' ? null : 'DECLINED';
    updateStatus(newStatus);
  };

  return {
    isLoading: isPending,
    currentStatus,
    handleApprove,
    handleReject,
    error,
  };
};
